import React, { useState } from 'react';
import { clsx } from 'clsx';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

export type StatusValue = 'pending' | 'in_progress' | 'completed';

// Ordinea de mutare a statusului
const STATUS_ORDER: StatusValue[] = ['pending', 'in_progress', 'completed'];

// Etichete în română
const STATUS_LABELS: Record<StatusValue, string> = {
  pending: 'De făcut',
  in_progress: 'În lucru',
  completed: 'Finalizat',
};

// Culori badge (amber / albastru / verde)
const STATUS_BADGE_STYLES: Record<StatusValue, string> = {
  pending: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
};

interface StatusMoverProps {
  value: StatusValue;
  onChange: (next: StatusValue) => void | Promise<void>;
  disabled?: boolean;
  size?: 'sm' | 'md';
  showLabel?: boolean;
  className?: string;
}

export function StatusMover({
  value,
  onChange,
  disabled = false,
  size = 'md',
  showLabel = true,
  className,
}: StatusMoverProps) {
  const [saving, setSaving] = useState(false);

  // Normalizare defensivă: dacă valoarea nu e cunoscută, tratăm ca 'pending'
  const currentIndex = Math.max(0, STATUS_ORDER.indexOf(value));
  const currentValue: StatusValue = STATUS_ORDER[currentIndex];

  const canGoLeft = currentIndex > 0;
  const canGoRight = currentIndex < STATUS_ORDER.length - 1;
  const isBusy = disabled || saving;

  const move = async (direction: -1 | 1) => {
    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= STATUS_ORDER.length) return;
    const next = STATUS_ORDER[nextIndex];
    if (next === currentValue) return;

    try {
      setSaving(true);
      await onChange(next);
    } finally {
      setSaving(false);
    }
  };

  const badgeSizeStyles =
    size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-xs';
  const btnSizeStyles = size === 'sm' ? 'p-1' : 'p-1.5';
  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  const buttonBase =
    'inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white';

  return (
    <div className={clsx('inline-flex items-center gap-1.5', className)}>
      <button
        type="button"
        onClick={() => move(-1)}
        disabled={isBusy || !canGoLeft}
        aria-label="Mută statusul înapoi"
        title="Mută statusul înapoi"
        className={clsx(buttonBase, btnSizeStyles)}
      >
        <ChevronLeft className={iconSize} />
      </button>

      <span
        className={clsx(
          'inline-flex items-center gap-1.5 font-medium rounded-full whitespace-nowrap',
          badgeSizeStyles,
          STATUS_BADGE_STYLES[currentValue]
        )}
      >
        {saving && <Loader2 className="w-3 h-3 animate-spin" />}
        {showLabel && STATUS_LABELS[currentValue]}
      </span>

      <button
        type="button"
        onClick={() => move(1)}
        disabled={isBusy || !canGoRight}
        aria-label="Mută statusul înainte"
        title="Mută statusul înainte"
        className={clsx(buttonBase, btnSizeStyles)}
      >
        <ChevronRight className={iconSize} />
      </button>
    </div>
  );
}

export default StatusMover;
